import { Stack } from "@mui/material";
import { useParams } from "react-router-dom";
import { usePortfolioQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioQuery";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { BankProductSection } from "web_myplann/Portfolio/Components/PortfolioTools/BankProductSection";
import { BondSection } from "web_myplann/Portfolio/Components/PortfolioTools/BondSection";
import { CommoditySection } from "web_myplann/Portfolio/Components/PortfolioTools/CommoditySection";
import { CryptoSection } from "web_myplann/Portfolio/Components/PortfolioTools/CryptoSection";
import { FundSection } from "web_myplann/Portfolio/Components/PortfolioTools/FundSection";
import {
	type NormalizedInstruction,
	type NormalizedTool,
	ToolKind,
} from "web_myplann/Portfolio/Helpers/investmentToolTypes";
import { normalizeTools } from "web_myplann/Portfolio/Helpers/normalizeTools";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import { getTypedParam } from "web_myplann/Shared/Utils/getTypedParam";
import { sortByClosestToToday } from "web_myplann/Shared/Utils/sortByClosestToToday";
import { DEFAULT_ROUTE } from "web_myplann/Utils/constants";

const getSortedRawInstructions = <TRaw,>(
	instructions: NormalizedInstruction<TRaw>[],
): TRaw[] =>
	sortByClosestToToday(instructions, "startAt").map(
		(instruction) => instruction.raw,
	);

const getToolHistory = (tool: NormalizedTool) => {
	switch (tool.kind) {
		case ToolKind.Investment:
			return (
				<FundSection
					investments={getSortedRawInstructions(tool.instructions)}
				/>
			);
		case ToolKind.Bond:
			return (
				<BondSection bonds={getSortedRawInstructions(tool.instructions)} />
			);
		case ToolKind.Commodity:
			return (
				<CommoditySection
					commodities={getSortedRawInstructions(tool.instructions)}
				/>
			);
		case ToolKind.Crypto:
			return (
				<CryptoSection crypto={getSortedRawInstructions(tool.instructions)} />
			);
		case ToolKind.BankProduct:
			return (
				<BankProductSection
					bankProducts={getSortedRawInstructions(tool.instructions)}
				/>
			);
		default:
			return null;
	}
};

export const ToolHistory = () => {
	const routeParams = useParams<{ id: string; toolId: string }>();
	const { t } = useResource();
	const portfolioIdTyped = getTypedParam(routeParams, "id", Number);
	const toolIdTyped = getTypedParam(routeParams, "toolId", Number);

	const { data: portfolioDetail } = usePortfolioQuery(portfolioIdTyped);

	const tool = normalizeTools(portfolioDetail?.tools).find(
		(item) => item.id === Number(toolIdTyped),
	);

	return (
		<MyPlannLayout
			topBar={
				<NavigationHeader
					title={t(Resources.PortfolioDetail.ToolHistory.title)}
					defaultRoute={DEFAULT_ROUTE}
				/>
			}
		>
			<Stack pt={2}>{tool && getToolHistory(tool)}</Stack>
		</MyPlannLayout>
	);
};
